import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  Plug,
  Unplug,
  FlaskConical,
} from 'lucide-react';
import { settingsAPI, hmrcAPI } from '../../api';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../hooks/useToast';
import { buildHmrcFraudContext } from '../../lib/hmrcFraudContext';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import HmrcSandboxTools from './hmrc/HmrcSandboxTools';

const ENVIRONMENTS = [
  { key: 'sandbox', label: 'Sandbox', description: 'HMRC test environment — safe for development' },
  { key: 'live', label: 'Live', description: 'Production HMRC — real tax data' },
];

function statusBadgeVariant(status, connected) {
  if (connected) return 'success';
  if (status === 'refresh_failed' || status === 'error') return 'destructive';
  if (status === 'disconnected') return 'secondary';
  return 'outline';
}

function statusLabel(status, connected) {
  if (connected) return 'Connected';
  if (status === 'not_configured') return 'Not connected';
  if (status === 'refresh_failed') return 'Reconnect required';
  if (status === 'error') return 'Error';
  if (status === 'disconnected') return 'Disconnected';
  return status || 'Unknown';
}

/**
 * Per-environment credential form fields.
 */
function EnvCredentialsForm({
  envKey,
  envLabel,
  config,
  suggestedCallbackUrl,
  values,
  onChange,
  showSecrets,
  onToggleShow,
  disabled,
}) {
  const prefix = envKey;
  const hasClientId = config?.hasClientId;
  const hasClientSecret = config?.hasClientSecret;

  return (
    <div className="space-y-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] p-4 bg-[var(--ds-surface-panel2)]">
      <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">{envLabel} app credentials</h3>
      <p className="text-micro text-[var(--ds-text-muted)]">
        Register a separate HMRC Developer Hub application for the {envLabel.toLowerCase()} environment.
      </p>

      <div>
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
          Client ID
          {hasClientId && <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>}
        </label>
        <div className="relative">
          <Input
            type={showSecrets[`${prefix}ClientId`] ? 'text' : 'password'}
            value={values[`${prefix}ClientId`] || ''}
            onChange={(e) => onChange(`${prefix}ClientId`, e.target.value)}
            placeholder={hasClientId ? 'Enter new Client ID to update' : 'Your HMRC Client ID'}
            disabled={disabled}
            className="h-8 pr-10 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            onClick={() => onToggleShow(`${prefix}ClientId`)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
            aria-label="Toggle visibility"
          >
            {showSecrets[`${prefix}ClientId`] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
          Client Secret
          {hasClientSecret && <span className="ml-2 text-[var(--ds-status-success)]">✓ Saved</span>}
        </label>
        <div className="relative">
          <Input
            type={showSecrets[`${prefix}ClientSecret`] ? 'text' : 'password'}
            value={values[`${prefix}ClientSecret`] || ''}
            onChange={(e) => onChange(`${prefix}ClientSecret`, e.target.value)}
            placeholder={hasClientSecret ? 'Enter new secret to update' : 'Your HMRC Client Secret'}
            disabled={disabled}
            className="h-8 pr-10 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
          <button
            type="button"
            onClick={() => onToggleShow(`${prefix}ClientSecret`)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]"
            aria-label="Toggle visibility"
          >
            {showSecrets[`${prefix}ClientSecret`] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
          Redirect URI
        </label>
        <Input
          type="url"
          value={values[`${prefix}RedirectUri`] ?? config?.redirectUri ?? suggestedCallbackUrl ?? ''}
          onChange={(e) => onChange(`${prefix}RedirectUri`, e.target.value)}
          placeholder={suggestedCallbackUrl || 'https://your-domain/api/hmrc/callback'}
          disabled={disabled}
          className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <p className="text-micro text-[var(--ds-text-muted)] mt-1">
          Must match the redirect URI registered in HMRC Developer Hub. Suggested:{' '}
          <code className="bg-[var(--ds-control-field-bg)] px-1 rounded text-xs break-all">
            {suggestedCallbackUrl || '/api/hmrc/callback'}
          </code>
        </p>
      </div>
    </div>
  );
}

/**
 * Connection panel for one environment (sandbox or live).
 */
function ConnectionPanel({
  envKey,
  envLabel,
  envDescription,
  connection,
  isAdmin,
  configReady,
  onConnect,
  onDisconnect,
  onTest,
  connecting,
  disconnecting,
  testing,
  isLive,
}) {
  const connected = connection?.connected;
  const status = connection?.status;

  return (
    <div
      className={`space-y-4 rounded-[var(--ds-radius-md)] border p-4 ${
        isLive
          ? 'border-amber-500/40 bg-amber-500/5 dark:bg-amber-500/10'
          : 'border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)]'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)] flex items-center gap-2">
            {isLive ? <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden /> : <FlaskConical className="h-5 w-5 text-[var(--ds-accent-primary)]" aria-hidden />}
            {envLabel}
          </h3>
          <p className="text-micro text-[var(--ds-text-muted)] mt-1">{envDescription}</p>
        </div>
        <Badge variant={statusBadgeVariant(status, connected)}>
          {statusLabel(status, connected)}
        </Badge>
      </div>

      {isLive && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-micro text-amber-800 dark:text-amber-200">
          Sandbox and Live are separate HMRC environments with separate apps and tokens. Connecting to Live uses real HMRC data.
        </div>
      )}

      {connected && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-micro">
          {connection.vrn && (
            <div>
              <span className="text-[var(--ds-text-muted)]">VRN: </span>
              <span className="font-medium text-[var(--ds-text-primary)]">{connection.vrn}</span>
            </div>
          )}
          {connection.scopes?.length > 0 && (
            <div className="md:col-span-2">
              <span className="text-[var(--ds-text-muted)]">Scopes: </span>
              <span className="font-mono text-xs text-[var(--ds-text-primary)]">
                {connection.scopes.join(', ')}
              </span>
            </div>
          )}
          {connection.tokenExpiresAt && isAdmin && (
            <div>
              <span className="text-[var(--ds-text-muted)]">Token expires: </span>
              <span className="text-[var(--ds-text-primary)]">
                {new Date(connection.tokenExpiresAt).toLocaleString()}
              </span>
            </div>
          )}
          {connection.lastSuccessfulCallAt && isAdmin && (
            <div>
              <span className="text-[var(--ds-text-muted)]">Last API call: </span>
              <span className="text-[var(--ds-text-primary)]">
                {new Date(connection.lastSuccessfulCallAt).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      )}

      {isAdmin && connection?.lastErrorMessage && !connected && (
        <p className="text-micro text-[var(--ds-status-danger)]">{connection.lastErrorMessage}</p>
      )}

      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          {!connected ? (
            <Button
              type="button"
              onClick={() => onConnect(envKey)}
              disabled={!configReady || connecting}
              className="gap-2"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              Connect {envLabel}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onTest(envKey)}
                disabled={testing}
                className="gap-2"
              >
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Test connection
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onDisconnect(envKey)}
                disabled={disconnecting}
                className="gap-2"
              >
                {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unplug className="h-4 w-4" />}
                Disconnect
              </Button>
            </>
          )}
        </div>
      )}

      {!isAdmin && (
        <p className="text-micro text-[var(--ds-text-muted)]">
          Only tenant administrators can connect or disconnect HMRC.
        </p>
      )}

      {!configReady && isAdmin && !connected && (
        <p className="text-micro text-[var(--ds-text-muted)]">
          Save {envLabel.toLowerCase()} Client ID, Client Secret, and Redirect URI before connecting.
        </p>
      )}
    </div>
  );
}

export default function HmrcSettings() {
  const { user, isTenantAdmin } = useAuthStore();
  const { success: toastSuccess, error: toastError, ToastContainer } = useToast();
  const isAdmin = isTenantAdmin();

  const [integration, setIntegration] = useState(null);
  const [status, setStatus] = useState({ sandbox: null, live: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectingEnv, setConnectingEnv] = useState(null);
  const [disconnectingEnv, setDisconnectingEnv] = useState(null);
  const [testingEnv, setTestingEnv] = useState(null);

  const [form, setForm] = useState({
    sandboxClientId: '',
    sandboxClientSecret: '',
    sandboxRedirectUri: '',
    liveClientId: '',
    liveClientSecret: '',
    liveRedirectUri: '',
    vendorVersion: '',
    vendorLicenseId: '',
  });
  const [dirty, setDirty] = useState({});
  const [showSecrets, setShowSecrets] = useState({});

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [integrationRes, statusRes] = await Promise.all([
        settingsAPI.getHmrcIntegration(),
        hmrcAPI.getStatus(),
      ]);
      setIntegration(integrationRes);
      setStatus({
        sandbox: statusRes.sandbox || null,
        live: statusRes.live || null,
      });
      setForm((prev) => ({
        ...prev,
        sandboxRedirectUri:
          prev.sandboxRedirectUri ||
          integrationRes?.sandbox?.redirectUri ||
          integrationRes?.suggestedCallbackUrl ||
          '',
        liveRedirectUri:
          prev.liveRedirectUri ||
          integrationRes?.live?.redirectUri ||
          integrationRes?.suggestedCallbackUrl ||
          '',
        vendorVersion: integrationRes?.vendorVersion || '',
        vendorLicenseId: integrationRes?.vendorLicenseId || '',
      }));
    } catch (err) {
      toastError(
        'Failed to load HMRC settings',
        err.response?.data?.error || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty((prev) => ({ ...prev, [field]: true }));
  };

  const toggleShow = (key) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resolveRedirectUriForSave = (envKey) => {
    const formUri = form[`${envKey}RedirectUri`]?.trim();
    if (formUri) return formUri;
    const savedUri = integration?.[envKey]?.redirectUri?.trim();
    if (savedUri) return savedUri;
    return integration?.suggestedCallbackUrl?.trim() || '';
  };

  const appendEnvCredentials = (envKey, target) => {
    const clientIdKey = `${envKey}ClientId`;
    const clientSecretKey = `${envKey}ClientSecret`;
    const redirectUriKey = `${envKey}RedirectUri`;

    if (dirty[clientIdKey] && form[clientIdKey]?.trim()) {
      target.clientId = form[clientIdKey].trim();
    }
    if (dirty[clientSecretKey] && form[clientSecretKey]?.trim()) {
      target.clientSecret = form[clientSecretKey].trim();
    }
    if (dirty[redirectUriKey]) {
      target.redirectUri = form[redirectUriKey].trim();
    } else if (dirty[clientIdKey] || dirty[clientSecretKey]) {
      const redirectUri = resolveRedirectUriForSave(envKey);
      if (redirectUri) target.redirectUri = redirectUri;
    }
  };

  const buildSavePayload = () => {
    const payload = {};

    const sandbox = {};
    appendEnvCredentials('sandbox', sandbox);
    if (Object.keys(sandbox).length) payload.sandbox = sandbox;

    const live = {};
    appendEnvCredentials('live', live);
    if (Object.keys(live).length) payload.live = live;

    if (dirty.vendorVersion) {
      payload.vendorVersion = form.vendorVersion.trim() || null;
    }
    if (dirty.vendorLicenseId) {
      payload.vendorLicenseId = form.vendorLicenseId.trim() || null;
    }

    return payload;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = buildSavePayload();
    if (!Object.keys(payload).length) return;

    setSaving(true);
    try {
      const res = await settingsAPI.updateHmrcIntegration(payload);
      setIntegration(res);
      setDirty({});
      setForm((prev) => ({
        ...prev,
        sandboxClientId: '',
        sandboxClientSecret: '',
        liveClientId: '',
        liveClientSecret: '',
        sandboxRedirectUri: res?.sandbox?.redirectUri || prev.sandboxRedirectUri,
        liveRedirectUri: res?.live?.redirectUri || prev.liveRedirectUri,
      }));
      toastSuccess('HMRC settings saved');
    } catch (err) {
      toastError('Save failed', err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const envConfigReady = (envKey) => {
    const cfg = integration?.[envKey];
    const savedRedirectUri = cfg?.redirectUri?.trim();
    const pendingRedirectUri = dirty[`${envKey}RedirectUri`]
      ? form[`${envKey}RedirectUri`]?.trim()
      : null;
    return !!(cfg?.hasClientId && cfg?.hasClientSecret && (savedRedirectUri || pendingRedirectUri));
  };

  const handleConnect = async (environment) => {
    setConnectingEnv(environment);
    try {
      const fraudContext = buildHmrcFraudContext(user?.id);
      const { authUrl } = await hmrcAPI.getConnectUrl(environment, fraudContext);
      window.location.href = authUrl;
    } catch (err) {
      toastError(
        'Could not start HMRC connection',
        err.response?.data?.error || err.message
      );
      setConnectingEnv(null);
    }
  };

  const handleDisconnect = async (environment) => {
    if (!window.confirm(`Disconnect from HMRC ${environment}?`)) return;
    setDisconnectingEnv(environment);
    try {
      await hmrcAPI.disconnect(environment);
      toastSuccess('Disconnected', `Disconnected from HMRC ${environment}.`);
      await loadData();
    } catch (err) {
      toastError('Disconnect failed', err.response?.data?.error || err.message);
    } finally {
      setDisconnectingEnv(null);
    }
  };

  const handleTest = async (environment) => {
    setTestingEnv(environment);
    try {
      const fraudContext = buildHmrcFraudContext(user?.id);
      const result = await hmrcAPI.testConnection(environment, fraudContext);
      toastSuccess(
        'HMRC test successful',
        `Found ${result.obligationsCount} VAT obligation(s). Fraud prevention: ${result.fraudPreventionValidation?.status || 'unknown'}.`
      );
      await loadData();
    } catch (err) {
      toastError('HMRC test failed', err.response?.data?.error || err.message);
    } finally {
      setTestingEnv(null);
    }
  };

  const hasDirtyFields = Object.values(dirty).some(Boolean);
  const suggestedCallbackUrl = integration?.suggestedCallbackUrl;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-[var(--ds-text-muted)]" />
          <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading HMRC settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-8">
        <div>
          <h2 className="text-h1 font-bold text-[var(--ds-text-primary)]">HMRC Making Tax Digital</h2>
          <p className="text-xs text-[var(--ds-text-secondary)] mt-1">
            Connect SparxMate to HMRC for VAT MTD. Each tenant uses their own HMRC Developer Hub applications.
          </p>
          <a
            href="https://developer.service.hmrc.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-micro text-[var(--ds-accent-primary)] mt-2 hover:underline"
          >
            HMRC Developer Hub
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        </div>

        {isAdmin ? (
          <form onSubmit={handleSave} className="space-y-6">
            {ENVIRONMENTS.map(({ key, label }) => (
              <EnvCredentialsForm
                key={key}
                envKey={key}
                envLabel={label}
                config={integration?.[key]}
                suggestedCallbackUrl={suggestedCallbackUrl}
                values={form}
                onChange={handleFieldChange}
                showSecrets={showSecrets}
                onToggleShow={toggleShow}
                disabled={saving}
              />
            ))}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vendor version (optional)
                </label>
                <Input
                  type="text"
                  value={form.vendorVersion}
                  onChange={(e) => handleFieldChange('vendorVersion', e.target.value)}
                  placeholder="SparxMate/1.0.0"
                  disabled={saving}
                  className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vendor license ID (optional)
                </label>
                <Input
                  type="text"
                  value={form.vendorLicenseId}
                  onChange={(e) => handleFieldChange('vendorLicenseId', e.target.value)}
                  placeholder="Gov-Vendor-License-IDs"
                  disabled={saving}
                  className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <Button type="submit" disabled={saving || !hasDirtyFields}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save credentials'
              )}
            </Button>
          </form>
        ) : (
          <p className="text-meta text-[var(--ds-text-muted)]">
            Only tenant administrators can configure HMRC app credentials.
          </p>
        )}

        <div className="space-y-4">
          <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">Connections</h3>
          {ENVIRONMENTS.map(({ key, label, description }) => (
            <ConnectionPanel
              key={key}
              envKey={key}
              envLabel={label}
              envDescription={description}
              connection={status[key]}
              isAdmin={isAdmin}
              configReady={envConfigReady(key)}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onTest={handleTest}
              connecting={connectingEnv === key}
              disconnecting={disconnectingEnv === key}
              testing={testingEnv === key}
              isLive={key === 'live'}
            />
          ))}
        </div>

        <HmrcSandboxTools isAdmin={isAdmin} />
      </CardContent>
      <ToastContainer />
    </Card>
  );
}
